import { useMemo } from "react";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { vehiclesService } from "./services/vehicles.service";
import { vehicleUsageService } from "./services/vehicle-usage.service";
import { vehicleAccessService } from "./services/vehicle-access.service";
import { vehicleSuspensionService } from "./services/vehicle-suspension.service";
import { VEHICLE_ACCESS_USER_SEARCH } from "./constants/vehicle-access-user-search";
import { referenceListsService } from "./services/reference-lists.service";
import type { VehicleUsageHistoryFilters } from "./types/vehicle-usage.types";
import type { ReferenceList } from "./types/reference-list.types";
import type { CoordinateDto } from "@/lib/api/generated/fleet/models/coordinateDto";
import type { VehicleUsagePermissionSettingDto } from "@/lib/api/generated/fleet/models/vehicleUsagePermissionSettingDto";
import type { Period } from "@/types/time.types";
import type { AccessMode } from "@/types/accessmode.types";
import type { Platform } from "@/types/platform.types";
import { usePlatform } from "@/hooks/use-platform/use-platform";

export interface VehicleSearchFilters {
	coordinates?: CoordinateDto;
	minimumSeats?: number;
	maxDistance?: number;
	minimumRange?: number;
	onlyInstantBookingPossible?: boolean;
	period?: Period;
	licensePlate?: string;
	subjectUserId?: string;
	subjectClientId?: string;
	accessMode?: AccessMode;
	expressBooking?: boolean;
	vehicleIds?: Array<string>;
	brandIds?: Array<string>;
	modelIds?: Array<string>;
	bodyStyleIds?: Array<string>;
	intentIds?: Array<string>;
	permissions?: Array<VehicleUsagePermissionSettingDto>;
	minDayPrice?: number;
	maxDayPrice?: number;
	onlyFavorites?: boolean;
	instantBooking?: boolean;
}

/**
 * Pure function to create vehicle query keys.
 * Single source of truth - used by both hooks and static functions.
 */
export function createVehicleKeys(platform?: Platform) {
	const keys = {
		all: () => [platform, "vehicles"] as const,

		lists: () => [...keys.all(), "list"] as const,

		adminList: () => [...keys.lists(), "admin"] as const,

		adminLite: () => [...keys.lists(), "admin-lite"] as const,

		adminActiveLite: () => [...keys.adminLite(), "active"] as const,

		clientList: (clientId?: string) =>
			[...keys.lists(), "by-client", clientId] as const,

		myVehicles: () => [...keys.all(), "my-vehicles"] as const,

		searches: () => [...keys.all(), "search"] as const,

		search: (filters: VehicleSearchFilters) =>
			[
				...keys.searches(),
				{
					coordinates: filters.coordinates,
					minimumSeats: filters.minimumSeats,
					maxDistance: filters.maxDistance,
					minimumRange: filters.minimumRange,
					onlyInstantBookingPossible: filters.onlyInstantBookingPossible,
					period: filters.period,
					licensePlate: filters.licensePlate,
					subjectUserId: filters.subjectUserId,
					subjectClientId: filters.subjectClientId,
					accessMode: filters.accessMode,
					expressBooking: filters.expressBooking,
					vehicleIds: filters.vehicleIds,
					brandIds: filters.brandIds,
					modelIds: filters.modelIds,
					bodyStyleIds: filters.bodyStyleIds,
					intentIds: filters.intentIds,
					permissions: filters.permissions,
					minDayPrice: filters.minDayPrice,
					maxDayPrice: filters.maxDayPrice,
					onlyFavorites: filters.onlyFavorites,
					instantBooking: filters.instantBooking,
				},
			] as const,

		details: () => [...keys.all(), "detail"] as const,

		detail: (vehicleId?: string) => [...keys.details(), vehicleId] as const,

		telematics: (vehicleId: string) =>
			[...keys.details(), vehicleId, "telematics"] as const,

		telematicsStatus: (vehicleId: string) =>
			[...keys.telematics(vehicleId), "status"] as const,

		batteryStatus: (vehicleId?: string) =>
			[...keys.telematicsStatus(vehicleId!), "battery"] as const,

		lockStatus: (vehicleId?: string) =>
			[...keys.telematics(vehicleId!), "lock-status"] as const,

		deviceInfo: (vehicleId?: string) =>
			[...keys.telematics(vehicleId!), "device-info"] as const,

		clientPreferences: (vehicleId?: string) =>
			[...keys.details(), vehicleId, "client-preferences"] as const,

		supportLink: (vehicleId?: string) =>
			[...keys.details(), vehicleId, "support-link"] as const,

		location: (vehicleId?: string) =>
			[...keys.details(), vehicleId, "location"] as const,

		providers: () => [...keys.all(), "providers"] as const,

		referenceList: (list: ReferenceList) =>
			[...keys.all(), "reference-list", list] as const,

		vehicleModels: () => [...keys.all(), "vehicle-models"] as const,

		devices: () => [...keys.all(), "devices"] as const,

		deviceByDeviceId: (deviceId?: string) =>
			[...keys.devices(), deviceId] as const,

		damages: (vehicleId?: string) =>
			[...keys.details(), vehicleId, "damages"] as const,

		damage3d: (vehicleId?: string) =>
			[...keys.details(), vehicleId, "damage-3d"] as const,

		events: (vehicleId?: string) =>
			[...keys.detail(vehicleId), "events"] as const,

		activeUsage: (vehicleId?: string) =>
			[...keys.detail(vehicleId), "active-usage"] as const,

		access: (vehicleId?: string) =>
			[...keys.detail(vehicleId), "access"] as const,

		// Under `access`, so the grant mutations' invalidation refreshes it too.
		accessChanges: (vehicleId?: string) =>
			[...keys.access(vehicleId), "changes"] as const,

		// Not under `detail`: saving the vehicle must not refetch these.
		suspensions: (vehicleId?: string) =>
			[...keys.all(), "suspensions", vehicleId] as const,

		suspension: (vehicleId?: string) =>
			[...keys.suspensions(vehicleId), "active"] as const,

		suspensionHistory: (vehicleId?: string) =>
			[...keys.suspensions(vehicleId), "history"] as const,

		suspensionAffectedBookings: (vehicleId?: string) =>
			[...keys.suspensions(vehicleId), "affected-bookings"] as const,

		suspendedVehicles: () => [...keys.lists(), "suspended"] as const,

		statusChangeBlockingBookings: (vehicleId?: string) =>
			[...keys.detail(vehicleId), "status-change-blocking-bookings"] as const,

		bookingsEndingAfter: (vehicleId?: string, from?: string) =>
			[...keys.detail(vehicleId), "bookings-ending-after", from] as const,

		openBookings: (vehicleId?: string) =>
			[...keys.detail(vehicleId), "open-bookings"] as const,

		giveAccess: () => [...keys.all(), "give-access"] as const,

		changeAccessLevel: () => [...keys.all(), "change-access-level"] as const,

		removeAccess: () => [...keys.all(), "remove-access"] as const,

		accessUserSearch: (userName: string) =>
			[...keys.all(), "access-user-search", userName] as const,

		activeUsages: () => [...keys.lists(), "active-usages"] as const,

		usages: (vehicleId?: string, filters?: VehicleUsageHistoryFilters) =>
			[...keys.detail(vehicleId), "usages", filters] as const,
		overdueUsages: () => [...keys.lists(), "overdue-usages"] as const,
	};
	return keys;
}

/**
 * Hook version - wraps createVehicleKeys with platform from usePlatform()
 */
export function useVehicleKeys() {
	const { platform } = usePlatform();
	return useMemo(() => createVehicleKeys(platform), [platform]);
}

/**
 * Pure function to create vehicle query options.
 * Single source of truth - used by both hooks and static functions.
 */
export function createVehicleQueryOptions(platform?: Platform) {
	const keys = createVehicleKeys(platform);

	return {
		adminList: () =>
			queryOptions({
				queryKey: keys.adminList(),
				queryFn: () => vehiclesService.getVehicles(),
			}),

		adminLite: () =>
			queryOptions({
				queryKey: keys.adminLite(),
				queryFn: () => vehiclesService.getVehiclesLite(),
			}),

		/** Only the vehicles currently in service. */
		adminActiveLite: () =>
			queryOptions({
				queryKey: keys.adminActiveLite(),
				queryFn: () => vehiclesService.getVehiclesLite({ activeOnly: true }),
			}),

		clientList: (clientId?: string) =>
			queryOptions({
				queryKey: keys.clientList(clientId),
				queryFn: () => vehiclesService.getClientVehicles(clientId!),
				enabled: !!clientId,
			}),

		myVehicles: () =>
			queryOptions({
				queryKey: keys.myVehicles(),
				queryFn: () => vehiclesService.getMyVehicles(),
			}),

		search: (filters: VehicleSearchFilters) =>
			queryOptions({
				queryKey: keys.search(filters),
				queryFn: () =>
					vehiclesService.searchVehicles({
						location: filters.coordinates
							? { coordinates: filters.coordinates }
							: undefined,
						minimumSeats: filters.minimumSeats,
						maxDistance: filters.maxDistance,
						minimumRange: filters.minimumRange,
						period: filters.period,
						subjectUserId: filters.subjectUserId,
						subjectClientId: filters.subjectClientId,
						accessMode: filters.accessMode,
						expressBooking: filters.expressBooking,
						vehicleIds: filters.vehicleIds,
						brandIds: filters.brandIds,
						modelIds: filters.modelIds,
						bodyStyleIds: filters.bodyStyleIds,
						intentIds: filters.intentIds,
						permissions: filters.permissions,
						minDayPrice: filters.minDayPrice,
						maxDayPrice: filters.maxDayPrice,
						onlyFavorites: filters.onlyFavorites,
						instantBooking: filters.instantBooking,
					}),
			}),

		detail: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.detail(vehicleId),
				queryFn: () => vehiclesService.getVehicle(vehicleId!),
				enabled: !!vehicleId,
			}),

		batteryStatus: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.batteryStatus(vehicleId),
				queryFn: () => vehiclesService.getBatteryStatus(vehicleId!),
				enabled: !!vehicleId,
			}),

		lockStatus: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.lockStatus(vehicleId),
				queryFn: () => vehiclesService.getLockStatus(vehicleId!),
				enabled: !!vehicleId,
			}),

		deviceInfo: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.deviceInfo(vehicleId),
				queryFn: () => vehiclesService.getVehicleDeviceInfo(vehicleId!),
				enabled: !!vehicleId,
			}),

		clientPreferences: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.clientPreferences(vehicleId),
				queryFn: () => vehiclesService.getVehicleClientPreferences(vehicleId!),
				enabled: !!vehicleId,
			}),

		referenceList: (list: ReferenceList) =>
			queryOptions({
				queryKey: keys.referenceList(list),
				queryFn: ({ signal }) =>
					referenceListsService.getReferenceList(list, signal),
				// The lists change with the catalogue, not per session.
				staleTime: 60 * 60 * 1000,
			}),

		vehicleModels: () =>
			queryOptions({
				queryKey: keys.vehicleModels(),
				queryFn: ({ signal }) => referenceListsService.getVehicleModels(signal),
				// The catalogue changes rarely; the booking engine caches it too.
				staleTime: 60 * 60 * 1000,
			}),

		supportLink: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.supportLink(vehicleId),
				queryFn: () => vehiclesService.getSupportLink(vehicleId!),
				enabled: !!vehicleId,
			}),

		location: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.location(vehicleId),
				queryFn: () => vehiclesService.getVehicleLocation(vehicleId!),
				enabled: !!vehicleId,
				placeholderData: keepPreviousData,
			}),

		providers: () =>
			queryOptions({
				queryKey: keys.providers(),
				queryFn: () => vehiclesService.fetchProviders(),
			}),

		deviceByDeviceId: (deviceId?: string) =>
			queryOptions({
				queryKey: keys.deviceByDeviceId(deviceId),
				queryFn: () => vehiclesService.getDeviceByDeviceId(deviceId!),
				enabled: !!deviceId,
			}),

		damages: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.damages(vehicleId),
				queryFn: () => vehiclesService.getDamagesForVehicle(vehicleId!),
				enabled: !!vehicleId,
			}),

		damage3d: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.damage3d(vehicleId),
				queryFn: () => vehiclesService.getVehicleDamage3DData(vehicleId!),
				enabled: !!vehicleId,
			}),

		activeUsage: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.activeUsage(vehicleId),
				queryFn: () => vehicleUsageService.getActiveVehicleUsage(vehicleId!),
				enabled: !!vehicleId,
				staleTime: 30_000,
			}),

		access: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.access(vehicleId),
				queryFn: () => vehicleAccessService.getVehicleAccess(vehicleId!),
				enabled: !!vehicleId,
			}),

		suspension: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.suspension(vehicleId),
				queryFn: ({ signal }) =>
					vehicleSuspensionService.getActiveSuspension(vehicleId!, signal),
				enabled: !!vehicleId,
			}),

		suspensionHistory: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.suspensionHistory(vehicleId),
				queryFn: ({ signal }) =>
					vehicleSuspensionService.getHistory(vehicleId!, signal),
				enabled: !!vehicleId,
			}),

		suspendedVehicles: () =>
			queryOptions({
				queryKey: keys.suspendedVehicles(),
				queryFn: ({ signal }) =>
					vehicleSuspensionService.getSuspendedVehicles(signal),
			}),

		// Computed live from the bookings: refetch whenever it is shown again.
		suspensionAffectedBookings: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.suspensionAffectedBookings(vehicleId),
				queryFn: ({ signal }) =>
					vehicleSuspensionService.getAffectedBookings(vehicleId!, signal),
				enabled: !!vehicleId,
				staleTime: 0,
			}),

		// Always fresh: the drawer checks again every time it opens.
		statusChangeBlockingBookings: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.statusChangeBlockingBookings(vehicleId),
				queryFn: () =>
					vehiclesService.getStatusChangeBlockingBookingIds(vehicleId!),
				enabled: !!vehicleId,
				staleTime: 0,
			}),

		bookingsEndingAfter: (vehicleId?: string, from?: string) =>
			queryOptions({
				queryKey: keys.bookingsEndingAfter(vehicleId, from),
				queryFn: () =>
					vehiclesService.getBookingsEndingAfter(vehicleId!, from!),
				enabled: !!vehicleId && !!from,
				staleTime: 0,
			}),

		openBookings: (vehicleId?: string) =>
			queryOptions({
				queryKey: keys.openBookings(vehicleId),
				queryFn: () => vehiclesService.getOpenBookings(vehicleId!),
				enabled: !!vehicleId,
				staleTime: 0,
				// The dialog stays open while ops works: a booking picked up meanwhile
				// has to turn into a block without a reload.
				refetchInterval: 30_000,
			}),

		accessUserSearch: (userName: string) =>
			queryOptions({
				queryKey: keys.accessUserSearch(userName),
				queryFn: () => vehicleAccessService.searchUsers(userName),
				enabled: userName.length >= VEHICLE_ACCESS_USER_SEARCH.minLength,
				staleTime: 30_000,
			}),

		activeUsages: () =>
			queryOptions({
				queryKey: keys.activeUsages(),
				queryFn: () => vehicleUsageService.getActiveVehicleUsages(),
				staleTime: 30_000,
			}),

		overdueUsages: () =>
			queryOptions({
				queryKey: keys.overdueUsages(),
				queryFn: () => vehicleUsageService.getOverdueVehicleUsages(),
				staleTime: 30_000,
			}),
	};
}

/**
 * Hook version - wraps createVehicleQueryOptions with platform from usePlatform()
 */
export function useVehicleQueryOptions() {
	const { platform } = usePlatform();
	return useMemo(() => createVehicleQueryOptions(platform), [platform]);
}
